import { useTranslation } from 'react-i18next';
import { AccountData, OrderData, OrderService, ResourceHoldData } from '../../../api';
import {
  calculateRequiredResourceCount,
  calculateResourceSelection,
} from '../../dialogs/EditOrderDialog/components/resourceSelectionPanel/resourceSelectionUtils';
import { ApiResponseHandlerType } from '../../../common/apiNotifications/ApiResponseHandlerContext';
import { ResourceValidationErrorType, validateResourceAssignment } from './resourceValidationUtils';
import {
  isResourceDropTarget,
  isUnassignedDropTarget,
  ResourceDropTarget,
  UnassignedDropTarget,
} from './DragDropTypes';
import { useAssignmentConflictHandler } from '../../../common/apiNotifications/responseHandlers/useAssignmentConflictHandler';
import { useAuth } from '../../../session/InternalAuthProvider';
import { useApiResponseHandlerContext } from '../../../common/apiNotifications/useApiResponseHandlerContext';

const calculateDragResourceSelection = (
  order: OrderData,
  targetResourceIndex: number,
  targetSchedulerConfigId: string,
  account: AccountData,
): number[] => {
  const config = account?.shared_resources?.scheduler_configs?.find(
    (candidate) => candidate.id === targetSchedulerConfigId,
  );
  if (!config) {
    return [targetResourceIndex];
  }

  const persons = order.persons || 1;

  // Get all available resource indexes for this config (simplified - assumes all are available for drag)
  const availableResourceIndexes = Array.from({ length: config.resource_count }, (_, i) => i);

  const calculation = calculateResourceSelection(targetResourceIndex, persons, config, availableResourceIndexes);

  if (calculation.isValid && calculation.selectedResources) {
    return calculation.selectedResources;
  }

  // Fallback to single resource if calculation fails
  return [targetResourceIndex];
};

type HandleResourceDropProps = {
  order: OrderData;
  dropTarget: ResourceDropTarget | UnassignedDropTarget;
  updateOrder: (order: OrderData, revalidate?: boolean) => void;
};

export const useHandleResourceDrop = () => {
  const { t } = useTranslation();
  const { executeWithConflictCheck } = useAssignmentConflictHandler();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { showResponseNotification } = useApiResponseHandlerContext();

  return async ({ order, dropTarget, updateOrder }: HandleResourceDropProps): Promise<void> => {
    let hold: ResourceHoldData | undefined;
    let validation: any = { isValid: true }; // Default to valid for unassigned case

    if (isResourceDropTarget(dropTarget)) {
      // Calculate multi-resource selection based on order requirements
      const targetResourceIndex = dropTarget.resourceIndex - 1; // Convert from 1-based to 0-based
      const resourceIndexes = calculateDragResourceSelection(
        order,
        targetResourceIndex,
        dropTarget.schedulerConfigId,
        account,
      );

      hold = { scheduler_config_id: dropTarget.schedulerConfigId, resource_indexes: resourceIndexes };

      // Client-side validation before API call
      validation = validateResourceAssignment(
        order,
        targetResourceIndex,
        dropTarget.schedulerConfigId,
        resourceIndexes,
        account,
      );

      if (!validation.isValid) {
        // Show validation error and return early (only for capacity issues)
        let errorMessage: string;
        const config = account?.shared_resources?.scheduler_configs?.find((c) => c.id === dropTarget.schedulerConfigId);
        switch (validation.errorType) {
          case ResourceValidationErrorType.INSUFFICIENT_SPACE:
            errorMessage = t('calendar.drag_resource.error_insufficient_space', {
              resourcesNeeded: calculateRequiredResourceCount(order.persons || 1, config?.persons_per_resource || 1),
              availableSpace: Math.max(0, (config?.resource_count || 0) - targetResourceIndex),
            });
            break;
          default:
            errorMessage = validation.errorMessage || t('calendar.drag_resource.error_generic');
        }

        showResponseNotification(errorMessage, ApiResponseHandlerType.ERROR);
        console.warn('Resource assignment validation failed:', validation);
        return;
      }

      // Show warning banner if there are warnings but allow the operation to proceed
      if (validation.warningMessage) {
        let warningMessage: string;
        switch (validation.warningType) {
          case ResourceValidationErrorType.WRONG_GROUP:
            warningMessage = `${t('calendar.drag_resource.warning_wrong_group')}`;
            break;
          case ResourceValidationErrorType.MASK_NOT_ALLOWED:
            warningMessage = `${t('calendar.drag_resource.warning_mask_not_allowed')}`;
            break;
          default:
            warningMessage = `${validation.warningMessage}`;
        }

        showResponseNotification(warningMessage, ApiResponseHandlerType.WARNING);
        console.warn('Resource assignment warning:', validation);
      }
    }

    const hasWarnings = !!validation.warningMessage;

    updateOrder(
      {
        ...order,
        manual_resource_holds: hold ? [hold] : [],
        resource_indexes: [],
      },
      false,
    ); // false = don't revalidate yet
    const updatedOrder = await executeWithConflictCheck(
      () =>
        OrderService.assignOrderToResource({
          order_id: order.id,
          manual_resource_holds: hold ? [hold] : [],
        }),
      isUnassignedDropTarget(dropTarget)
        ? t('calendar.assign_resource.successRemove')
        : t('calendar.assign_resource.success'),
      undefined, // finallyCallback
      hasWarnings, // suppressSuccessMessage when we have warnings
    );
    // calling twice so there is revalidation in case wasn't successfull
    updateOrder(updatedOrder);
  };
};
